import { MetricIds } from 'src/core/models/enum/metric-ids.enum';
import { MetricTrendResultDto } from 'src/core/models/dashboard/metric-trend-result.dto';
import { MetricValueResultDto } from 'src/core/models/dashboard/metric-value-result.dto';
import { TrendItemDto } from 'src/core/models/dashboard/trend-item.dto';
import { DashboardCardItem } from '../interfaces/dashboard-card-item.interface';
import { DashboardWidgetDefinition } from '../interfaces/dashboard-widget-definition.interface';
import { MetricEndpoints } from '../interfaces/metric-endpoints.interface';
import { WidgetDefinitionFromConstants } from '../interfaces/widget-definition-from-constants.interface';

export class DashboardHelper {
  static readonly DEFAULT_METRIC_TITLE_KEY = 'Dashboard::MetricDetails';
  static readonly DEFAULT_METRIC_DESCRIPTION_KEY = 'Dashboard::MetricDescription';

  static isMobile(): boolean {
    return window.innerWidth <= 768;
  }

  static isRtl(currentLang: string | undefined): boolean {
    const lang = currentLang?.toLowerCase() ?? '';
    return lang.startsWith('ar');
  }

  static isDarkTheme(): boolean {
    if (typeof document === 'undefined') return false;
    const body = document.body;
    return (
      document.documentElement.classList.contains('prime-dark') ||
      body?.getAttribute('data-bs-theme') === 'dark' ||
      body?.classList.contains('lpx-theme-dark') ||
      body?.classList.contains('lpx-theme-dim')
    );
  }

  static buildDefsByMetricId(
    defs: DashboardWidgetDefinition[],
  ): Map<MetricIds, DashboardWidgetDefinition> {
    const map = new Map<MetricIds, DashboardWidgetDefinition>();
    for (const d of defs) {
      if (d.metricId != null) map.set(d.metricId, d);
    }
    return map;
  }

  static getMainCardItems(defs: DashboardWidgetDefinition[]): DashboardCardItem[] {
    return defs
      .filter(d => d.layout === 'main')
      .map(d => ({
        id: d.id,
        component: d.component,
        isLarge: d.isLarge,
      }));
  }

  static getGroupedCardItems(defs: DashboardWidgetDefinition[]): DashboardCardItem[] {
    return defs
      .filter(d => d.layout === 'grouped')
      .map(d => ({
        id: d.id,
        component: d.component,
      }));
  }

  static getWidgetDefinitionsForDesktop(
    defs: DashboardWidgetDefinition[],
  ): WidgetDefinitionFromConstants[] {
    const main = defs.filter(d => d.layout === 'main');
    return main.map(d => ({
      id: d.id,
      titleKey: d.titleKey,
      descriptionKey: d.descriptionKey,
      icon: d.icon,
    }));
  }

  static getWidgetDefinitionsForMobile(
    defs: DashboardWidgetDefinition[],
  ): WidgetDefinitionFromConstants[] {
    const mainWithoutGrouped = defs.filter(d => d.layout === 'main' && !d.desktopOnly);
    const grouped = defs.filter(d => d.layout === 'grouped');
    const list = [mainWithoutGrouped[0], ...grouped, ...mainWithoutGrouped.slice(1)];
    return list.map(d => ({
      id: d.id,
      titleKey: d.titleKey,
      descriptionKey: d.descriptionKey,
      icon: d.icon,
    }));
  }

  static getAllCardItems(
    defs: DashboardWidgetDefinition[],
    isMobile: boolean,
  ): DashboardCardItem[] {
    if (isMobile) {
      const main = DashboardHelper.getMainCardItems(defs).filter(c => c.id !== 'grouped-metrics');
      return [...main, ...DashboardHelper.getGroupedCardItems(defs)];
    }
    return DashboardHelper.getMainCardItems(defs);
  }

  static getMetricTitleKey(
    defsByMetricId: Map<MetricIds, DashboardWidgetDefinition>,
    metricId: MetricIds,
  ): string {
    return defsByMetricId.get(metricId)?.titleKey ?? DashboardHelper.DEFAULT_METRIC_TITLE_KEY;
  }

  static getMetricDescriptionKey(
    defsByMetricId: Map<MetricIds, DashboardWidgetDefinition>,
    metricId: MetricIds,
  ): string {
    const d = defsByMetricId.get(metricId);
    return (
      d?.metricDescriptionKey ?? d?.descriptionKey ?? DashboardHelper.DEFAULT_METRIC_DESCRIPTION_KEY
    );
  }

  static getMetricIdByWidgetId(
    defs: DashboardWidgetDefinition[],
    widgetId: string,
  ): MetricIds | undefined {
    return defs.find(d => d.id === widgetId)?.metricId;
  }

  static buildMetricEndpoints(
    defs: DashboardWidgetDefinition[],
    extra: Partial<Record<MetricIds, MetricEndpoints>>,
  ): Record<MetricIds, MetricEndpoints> {
    const result = { ...extra } as Record<MetricIds, MetricEndpoints>;
    for (const d of defs) {
      if (d.metricId != null && d.endpoints) result[d.metricId] = d.endpoints;
    }
    return result;
  }

  private static readonly DEFAULT_PREVIEW_TREND_DATA: TrendItemDto[] = [
    { label: 'Mon', value: 12, count: 100 },
    { label: 'Tue', value: 18, count: 110 },
    { label: 'Wed', value: 22, count: 120 },
    { label: 'Thu', value: 16, count: 105 },
    { label: 'Fri', value: 20, count: 115 },
    { label: 'Sat', value: 14, count: 98 },
    { label: 'Sun', value: 19, count: 108 },
  ];

  static readonly DEFAULT_PREVIEW_TREND: MetricTrendResultDto = {
    updatedAt: new Date().toISOString(),
    data: DashboardHelper.DEFAULT_PREVIEW_TREND_DATA,
  };

  static isStale(date: Date | string | null | undefined): boolean {
    if (!date) return false;
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return false;
    return Date.now() - d.getTime() > 24 * 60 * 60 * 1000;
  }

  static getPreviewMetricValues(
    defs: DashboardWidgetDefinition[],
  ): Map<MetricIds, MetricValueResultDto> {
    const map = new Map<MetricIds, MetricValueResultDto>();
    const updatedAt = new Date();
    for (const d of defs) {
      if (d.metricId != null && d.previewValue) {
        map.set(d.metricId, {
          value: d.previewValue.value,
          totalCount: d.previewValue.totalCount,
          caUpdatedAt: updatedAt,
          vaUpdatedAt: updatedAt,
        });
      }
    }
    return map;
  }
}
